import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import { Box, Chip, Link, Avatar, Tooltip, Typography } from '@mui/material';
import { Folder, Lock, Tag } from 'lucide-mui';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers';
import { getFolderUrl } from '../../lib/routingUtils';

const getCreatedAtString = (createdAt: Date | string) => {
  const now = moment();
  const created = moment(createdAt);

  if (!created.isValid()) {
    return String(createdAt);
  }

  if (created.isSame(now, 'day')) {
    return 'Today';
  }

  if (created.isSame(now.clone().subtract(1, 'days'), 'day')) {
    return 'Yesterday';
  }

  if (created.isSame(now.clone().subtract(2, 'days'), 'day')) {
    return '2 days ago';
  }

  if (created.isSame(now.clone().subtract(3, 'days'), 'day')) {
    return '3 days ago';
  }

  return created.format('MMM D, YYYY');
};

interface ChipData {
  icon: React.ReactElement;
  label: string;
  url?: string;
}

interface MetaDataProps {
  file: {
    recorderName: string;
    createdAt: Date | string;
    parentId: string | null;
    owner?: {
      name: string;
      picture?: string;
    };
  };
}

const MetaData: React.FC<MetaDataProps> = ({ file }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [showExtraChips, setShowExtraChips] = useState(true);
  const [showNameAndDate, setShowNameAndDate] = useState(true);
  const { selectedTeam, teams } = useSelector((state: RootState) => state.team);
  const teamId = selectedTeam?.teamId || (teams[0]?.teamId as string);
  const { owner } = file;

  useEffect(() => {
    const updateVisibility = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        setShowExtraChips(containerWidth > 400);
        setShowNameAndDate(containerWidth > 150);
      }
    };

    const resizeObserver = new ResizeObserver(updateVisibility);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    window.addEventListener('resize', updateVisibility);

    return () => {
      if (containerRef.current) {
        resizeObserver.unobserve(containerRef.current);
      }
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateVisibility);
    };
  }, []);

  const postProcessType = useSelector((state: RootState) => {
    const response =
      state.selectedVideo.postProcess?.systemPromptResponses
        ?.SUMMARY_AND_PROMPTS?.responseText;
    if (response) {
      try {
        const parsed = JSON.parse(response);
        return parsed.transcript_type || '';
      } catch (e) {
        return '';
      }
    }
    return '';
  });

  const parentFolder = useSelector((state: RootState) => {
    const parentId = state.selectedVideo.file?.parentId;
    return state.library.folders.data.find((folder) => folder._id === parentId);
  });

  const getFolderChip = (): ChipData => {
    if (!parentFolder) {
      return {
        icon: <Lock sx={{ height: '14px', width: '14px' }} />,
        label: 'Private',
        url: getFolderUrl({
          teamId,
          folderId: 'private',
          withBasePath: true,
        }),
      };
    }

    return {
      icon: <Folder sx={{ height: '14px', width: '14px' }} />,
      label:
        parentFolder.name.length > 10
          ? `${parentFolder.name.slice(0, 10)}...`
          : parentFolder.name,
      url: getFolderUrl({
        teamId,
        folderId: parentFolder._id,
        withBasePath: true,
      }),
    };
  };

  const chipData: ChipData[] = [getFolderChip()];

  if (postProcessType) {
    chipData.unshift({
      icon: <Tag sx={{ height: '14px', width: '14px' }} />,
      label: postProcessType || '',
    });
  }

  const commonChipStyles = {
    backgroundColor: 'transparent',
    border: 'none',
    flexShrink: 0,
    opacity: 0.7,
    '&:hover': {
      opacity: 1,
    },
  };

  const tooltipContent = (
    <Box sx={{ p: 0.5 }}>
      <Typography variant="body2">
        {owner?.name || file.recorderName}
      </Typography>
      <Typography variant="caption" sx={{ opacity: 0.8 }}>
        {getCreatedAtString(file.createdAt)}
      </Typography>
    </Box>
  );

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        width: '100%',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          minWidth: 0,
          flexShrink: 0,
        }}
      >
        <Tooltip
          title={
            showNameAndDate ? owner?.name || file.recorderName : tooltipContent
          }
          arrow
          placement="top"
        >
          <Avatar
            alt={owner?.name || file.recorderName}
            src={owner?.picture}
            sx={{
              width: '36px',
              height: '36px',
              backgroundColor: 'secondary.main',
              transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'scale(1.1)',
              },
            }}
          />
        </Tooltip>
        {showNameAndDate && (
          <Box>
            <Chip
              label={owner?.name || file.recorderName}
              color="secondary"
              size="small"
              sx={{
                ...commonChipStyles,
                maxWidth: '200px',
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
              }}
            />
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.6rem',
                color: 'text.secondary',
                opacity: 0.8,
                mt: '2px',
                pl: '8px',
              }}
            >
              {getCreatedAtString(file.createdAt)}
            </Typography>
          </Box>
        )}
      </Box>
      {showExtraChips && (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            overflowX: 'hidden',
            flexWrap: 'nowrap',
            alignItems: 'center',
            minWidth: 0,
            flexShrink: 1,
          }}
        >
          {chipData.map((chip, index) => {
            const ChipElement = (
              <Chip
                key={index}
                icon={chip.icon}
                label={chip.label}
                color="secondary"
                size="small"
                sx={commonChipStyles}
              />
            );

            return chip.url ? (
              <Link
                key={index}
                href={chip.url}
                underline="none"
                sx={{ display: 'flex' }}
              >
                {ChipElement}
              </Link>
            ) : (
              ChipElement
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default MetaData;
